import { useCallback, useEffect, useState } from "react"

import { WarningIcon } from "@chakra-ui/icons"
import { Box, Button, Center, Text } from "@chakra-ui/react"
import { useSearchParams } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "app/hooks"
import CustomSearch from "components/custom-search-input/customsearch"
import CustomMenuItem from "components/menudropdown/dropdown-menu-item"
import MenuDropDown from "components/menudropdown/menudropdown"
import FeeMappingTable, {
  FeeMappingItem,
} from "components/table/fee-mapping-table"
import PageSelector from "components/table/page-selector"
import TemplateHeader from "components/template/template-header"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"
import { CategoryData } from "services/common"
import { UpdateCategoryResponse } from "services/fee-mapping"
import { selectCurrentPmc } from "store/auth/authSlice"
import {
  feeCategories,
  feeMappingCategories,
} from "store/features/featuresSlice"

import FeeMappingPagination from "./FeeMappingPagination"

export type SortOrder = "ASC" | "DESC"
export const ASC = "ASC"
export const DESC = "DESC"
export const defautCategory = "feeName"

const FeeMapping = () => {
  const [pageSize, setPageSize] = useState<number>(5)
  const [searchParams, setSearchParams] = useSearchParams()

  const [term, setTerm] = useState<string>(searchParams.get("keyword") || "")
  const getFeeCategories = useAppSelector(feeCategories)

  const [pageStartIndex, setPageStartIndex] = useState<number>(0)
  const [updatedCategoryData, setUpdatedCategoryData] = useState<
    FeeMappingItem[]
  >([])

  const [categoryData, setCategoryData] = useState<CategoryData[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isUpdatedCategoryLoading, setIsUpdatedCategoryLoading] =
    useState<boolean>(false)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof CategoryData | null
    order: SortOrder
  }>({ key: defautCategory, order: ASC })
  const [selectedCategories, setSelectedCategories] = useState<string>("ALL")

  const dispatch = useAppDispatch()
  const currentPmc = useAppSelector(selectCurrentPmc)

  const displayToast = useToastHook()
  // const displayToast = useToastHook()

  useEffect(() => {
    setPageStartIndex(0)
  }, [currentPmc])

  useEffect(() => {
    setTerm("")
  }, [])

  useEffect(() => {
    // Set a query parameter
    setSearchParams({
      keyword: term,
      noOfRecords: `${pageSize}`, // Convert number to string using template literal
      pageNo: `${pageStartIndex}`, // Convert number to string using template literal
    })
  }, [setSearchParams, term, pageSize, pageStartIndex])

  const fetchFeeCategoryData = useCallback(async () => {
    setIsLoading(true)

    if (currentPmc?.pmc_id) {
      try {
        const response = await apiClient.getCategoryData({
          params: {
            pmcId: currentPmc?.pmc_id,
            categoryName: selectedCategories,
            keyword: term,
            limit: pageSize,
            skip: pageSize * pageStartIndex,
            sortBy: sortConfig.key || "",
            sortDirection: sortConfig.order,
          },
        })
        setCategoryData(response.data.data)
      } catch (err) {
        console.log(err)
      } finally {
        setIsLoading(false)
      }
    }
  }, [
    currentPmc?.pmc_id,
    pageSize,
    pageStartIndex,
    selectedCategories,
    sortConfig.key,
    sortConfig.order,
    term,
  ])

  useEffect(() => {
    fetchFeeCategoryData()
      .then()
      .catch((e) => console.log(e))
  }, [fetchFeeCategoryData])

  useEffect(() => {
    dispatch(feeMappingCategories())
      .then(() => true)
      .catch((e) => console.log(e))
  }, [dispatch])

  const updateCategoryHandler = async () => {
    if (currentPmc?.pmc_id) {
      setIsUpdatedCategoryLoading(true)
      try {
        await apiClient
          .updateFeeCategoryData({
            params: { pmcId: currentPmc?.pmc_id },
            data: updatedCategoryData,
          })
          .then(async (response: UpdateCategoryResponse) => {
            if (response?.data?.apiStatus.toLowerCase() !== "success") {
              displayToast({
                message: response?.data?.errorMessage || "",
                variant: "error",
              })
            }
            setIsUpdatedCategoryLoading(false)
            setUpdatedCategoryData([])
            await fetchFeeCategoryData()

            return true
          })
      } catch (error) {
        console.log(error)
        setIsUpdatedCategoryLoading(false)
      }
    }
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategories(category)
    setPageStartIndex(0)
  }

  return (
    <Box>
      <TemplateHeader text='Fee Mapping' />

      <Box display='flex' gap={3}>
        <Box flexGrow={1} maxW={{ base: "full", sm: "320px" }}>
          <CustomSearch
            width='full'
            term={term}
            setTerm={(e: string) => {
              setTerm(e)
              setPageStartIndex(0)
            }}
            onClear={() => setTerm("")}
          />
        </Box>

        <MenuDropDown
          maxH={{ base: "300px", xxxl: "400px" }}
          title={selectedCategories || "ALL"}
          borderRadius='50px'
          buttonWidth='200px'
        >
          {["ALL", ...getFeeCategories].map((category: string) => (
            <CustomMenuItem
              key={category}
              value={category}
              onClick={() => handleCategoryChange(category)}
            >
              {category}
            </CustomMenuItem>
          ))}
        </MenuDropDown>
      </Box>
      {!isLoading && categoryData?.length === 0 ? (
        <Center p={8} w='full' flexDir='column'>
          <WarningIcon color='primaryColor' w={16} h={16} />
          <Text size='lg' mt={8} mb={4}>
            No Fee yet.
          </Text>
        </Center>
      ) : (
        <Box>
          <FeeMappingTable
            categoryData={categoryData}
            isLoading={isLoading}
            pageSize={pageSize}
            setUpdatedCategoryData={setUpdatedCategoryData}
            onSort={setSortConfig}
          />
          <Box display='flex' flexDirection='column' gap={4} mt={4}>
            <Box
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              gap={4}
            >
              <PageSelector
                pageSize={pageSize}
                setPageSize={setPageSize}
                setPageStartIndex={setPageStartIndex}
              />

              <FeeMappingPagination
                pageSize={pageSize}
                itemsLength={categoryData.length}
                pageStartIndex={pageStartIndex}
                setPageStartIndex={setPageStartIndex}
              />
            </Box>
            <Button
              width='10%'
              height='40px'
              alignSelf='flex-end'
              onClick={updateCategoryHandler}
              disabled={
                updatedCategoryData.length === 0 || isUpdatedCategoryLoading
              }
              isLoading={isUpdatedCategoryLoading}
              mb='20px'
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default FeeMapping
